import { create } from "zustand"
import { NominatimResponse } from "./nominatim"
import axios from "axios"
import { indications } from "../steps/insurances"


export enum STEPS {
  diagnoseAndInsurance = "Step1",
  unfittingInsurance = "Step2b",
  doctorsOfficeClose = "Step3b",
  fittingInsurance = "Step2a",
  helpWasAskedFor = "Step3a",
  confirmationUnfitting = "Step4a",
}

interface Address {
  street: string
  houseNumber: string
  city: string
}

export enum supportOptions {
  reimbursement = "reimbursement",
  selfPayment = "selfPayment",
  switchInsurance = "switchInsurance",
}

// Define types for the user store
export interface UserState {
  uuid: string | null
  firstName: string
  lastName: string
  address: Address | null
  mail: string
  phone: string
  plz: string
  nominatimResponse: NominatimResponse | null
  confirmSkinuvitaInterest: boolean
  confirmDataUsage: boolean
  diagnoses: indications[]
  otherDiagnosis: string | null
  healthInsurance: string
  supportOptions: string[]

  setUuid: (string) => void
  setConfirmSkinuvitaInterest: (confirmSkinuvitaInterest: boolean) => void
  setConfirmDataUsage: (confirmDataUsage: boolean) => void
  setFirstName: (firstName: string) => void
  setLastName: (lastName: string) => void
  setAddress: (address: Address) => void
  setMail: (mail: string) => void
  setPhone: (phone: string) => void
  setPLZ: (plz: string) => void
  setNominatimResponse: (nominatimResponse: NominatimResponse) => void
  setDiagnoses: (diagnoses: indications[]) => void
  toggleDiagnosis: (diagnoses: indications) => void
  setOtherDiagnosis: (otherDiagnosis: indications | null) => void
  setHealthInsurance: (healthInsurance: string) => void
  toggleSupportOption: (supportOption: string) => void
  resetUserStore: () => void
}

const initialState = {
  confirmSkinuvitaInterest: false,
  confirmDataUsage: false,
  firstName: "",
  lastName: "",
  address: null,
  mail: "",
  phone: "",
  plz: "",
  nominatimResponse: null,
  diagnoses: [],
  otherDiagnosis: null,
  healthInsurance: "",
  supportOptions: [],
  uuid: null,
}

// Create the user store
const useUserStore = create<UserState>((set) => ({
  confirmSkinuvitaInterest: false,
  confirmDataUsage: false,
  firstName: "",
  lastName: "",
  address: null,
  mail: "",
  phone: "",
  plz: "",
  nominatimResponse: null,
  diagnoses: [],
  otherDiagnosis: null,
  healthInsurance: "",
  supportOptions: [],
  uuid: null,

  setUuid: (uuid) => set({ uuid }),
  setFirstName: (firstName) => set({ firstName }),
  setLastName: (lastName) => set({ lastName }),
  setAddress: (address) => set({ address }),
  setMail: (mail) => set({ mail }),
  setPhone: (phone) => set({ phone }),
  setPLZ: (plz) => set({ plz }),
  setNominatimResponse: (nominatimResponse) => set({ nominatimResponse }),
  setConfirmSkinuvitaInterest: (confirmSkinuvitaInterest) => set({ confirmSkinuvitaInterest }),
  setConfirmDataUsage: (confirmDataUsage) => set({ confirmDataUsage }),
  setDiagnoses: (diagnoses) => set({ diagnoses }),

  toggleDiagnosis: (diagnosis) =>
    set((state) => ({
      diagnoses: state.diagnoses.includes(diagnosis)
        ? state.diagnoses.filter((d) => d !== diagnosis) // Remove if already selected
        : [...state.diagnoses, diagnosis], // Add if not selected
    })),

  setOtherDiagnosis: (otherDiagnosis) =>
    set((state) => {
      // Remove any existing "other" diagnosis
      const filteredDiagnoses = state.diagnoses.filter(
        (item) => item == indications.psoriasis || item == indications.dermatitis || item == indications.hand_eczema
      )

      console.log("filteredDiagnoses", filteredDiagnoses)

      // Add the new diagnosis if it's not empty
      return {
        diagnoses: otherDiagnosis?.trim() ? [...filteredDiagnoses, otherDiagnosis] : filteredDiagnoses,
        otherDiagnosis: otherDiagnosis?.trim() || null,
      }
    }),
  setHealthInsurance: (healthInsurance) => set({ healthInsurance }),
  toggleSupportOption: (supportOption) =>
    set((state) => ({
      supportOptions: state.supportOptions.includes(supportOption)
        ? state.supportOptions.filter((option) => option !== supportOption)
        : [...state.supportOptions, supportOption],
    })),
  resetUserStore: () =>
    set({
      confirmSkinuvitaInterest: false,
      confirmDataUsage: false,
      firstName: "",
      lastName: "",
      address: null,
      mail: "",
      phone: "",
      plz: "",
      nominatimResponse: null,
      diagnoses: [],
      otherDiagnosis: null,
      healthInsurance: "",
      supportOptions: [],
      uuid: null,
    }),
}))

// Define types for the wizard store
interface WizardState {
  step: STEPS
  setStep: (step: STEPS) => void
}

// Create the wizard store
const useWizardStore = create<WizardState>((set) => ({
  step: STEPS.diagnoseAndInsurance,
  setStep: (step) => set({ step }),
}))

export { useUserStore, useWizardStore }
